'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Logo from '../Logo';
import { GROQHeader } from '@/sanity/lib/definitions';
import { usePathname } from 'next/navigation';
import { IPageLink, resolvePageLink } from '@/sanity/globals/CTA';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useMotionValueEvent,
  useTransform,
  easeInOut,
} from 'framer-motion';
import { CTAButton } from '@/shadcn/ui/button';
import LanguageSwitcher from './LanguageSwithcer';
import { ChevronDown } from 'lucide-react';
import menuImage from '@/public/global/menuImage.png';
import Image from 'next/image';

export default function Header({
  headerData,
}: {
  headerData: GROQHeader;
}) {
  const pathname = usePathname();
  const DEFAULT_LANGUAGE = 'de';
  const lang = useMemo(() => {
    if (pathname?.startsWith('/en')) return 'en';
    if (pathname?.startsWith('/de')) return 'de';
    return DEFAULT_LANGUAGE;
  }, [pathname]);

  const hasLangPrefix = useMemo(
    () => pathname?.startsWith('/en') || pathname?.startsWith('/de'),
    [pathname],
  );

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const { scrollY } = useScroll();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 0);
  });

  const bgVariants = useMemo(
    () =>
      shouldReduceMotion
        ? {
            visible: { opacity: 1 },
            hidden: { opacity: 0 },
          }
        : {
            visible: {
              opacity: 1,
              transition: { duration: 0.3, ease: 'easeOut' },
            },
            hidden: {
              opacity: 0,
              transition: { duration: 0.2, ease: 'easeIn' },
            },
          },
    [shouldReduceMotion],
  );

  return (
    <div className="absolute lg:top-[3rem] w-full">
      <motion.header
        className="container w-full lg:max-w-[230.01rem] p-[3rem] pb-[2rem] pt-[2.5rem] pr-[2.5rem] lg:pr-[2rem] lg:pl-[11rem] relative z-[10000] border-b border-white/[8%] lg:border-b-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: easeInOut, delay: 0.25 }}
        whileInView="visible"
        viewport={{ once: true }}
      >
        <NavbarContent
          pathname={pathname}
          headerData={headerData}
          lang={lang}
          hasLangPrefix={hasLangPrefix}
          isMobileMenuOpen={isMobileMenuOpen}
          onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
          onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
        />
      </motion.header>

      <MobileMenu
        headerData={headerData}
        lang={lang}
        hasLangPrefix={hasLangPrefix}
        pathname={pathname}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </div>
  );
}

function addLanguageToLink(link: string, lang: string, hasLangPrefix: boolean) {
  if (!hasLangPrefix) return link;

  // Don't add language prefix to external links
  if (
    link.startsWith('http://') ||
    link.startsWith('https://') ||
    link.startsWith('mailto:') ||
    link.startsWith('tel:')
  ) {
    return link;
  }

  const langPrefix = `/${lang}`;

  if (link.startsWith('#')) {
    return `${langPrefix}${link}`;
  }

  if (link === '/') {
    return langPrefix;
  }

  if (link.includes('#')) {
    const [path, anchor] = link.split('#');
    if (path === '' || path === '/') {
      return `${langPrefix}#${anchor}`;
    }
    return `${langPrefix}${path}#${anchor}`;
  }

  return `${langPrefix}${link}`;
}

function getMenuItems(headerData: GROQHeader) {
  return headerData?.menu?.items ?? [];
}

function NavbarContent({
  headerData,
  lang,
  hasLangPrefix,
  isMobileMenuOpen,
  onOpenMobileMenu,
  onCloseMobileMenu,
  pathname,
}: {
  headerData: GROQHeader;
  lang: 'de' | 'en';
  hasLangPrefix: boolean;
  isMobileMenuOpen: boolean;
  onOpenMobileMenu: () => void;
  onCloseMobileMenu: () => void;
  pathname?: string;
}) {
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const [logoVariant, setLogoVariant] = useState<'default' | 'black'>('black');
  const menuItems = getMenuItems(headerData);

  useEffect(() => {
    if (isMobileMenuOpen) {
      // Quando abre, muda imediatamente para default
      setLogoVariant('default');
    } else {
      // Quando fecha, espera 600ms antes de mudar para black
      const timeout = setTimeout(() => {
        setLogoVariant('black');
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isMobileMenuOpen]);

  return (
    <nav className="w-full flex items-center justify-between">
      <div className="flex lg:gap-[8rem] items-center justify-start w-fit">
        <Link
          className="block w-fit  justify-self-start"
          aria-label="RevUp Logo"
          href="/"
        >
          <Logo className="w-fit" variantText={logoVariant} />
        </Link>

        <ul className="hidden lg:flex gap-[7rem] w-fit items-center h-full">
          {menuItems.map((item, index) => {
            const itemLink = addLanguageToLink(
              resolvePageLink(item?.link as IPageLink) ?? '/',
              lang,
              hasLangPrefix,
            );

            const type = item?.type;
            const isDropdown = type === 'dropdown' && item?.dropdownLinks;

            const isActive =
              itemLink === '/' || itemLink === '/en'
                ? pathname === itemLink
                : pathname === itemLink || pathname?.startsWith(itemLink + '/');
            const openInNewTab = item?.link?.openInNewTab;

            return (
              <li key={index}>
                {isDropdown && (
                  <div className="relative group">
                    <button
                      onClick={() =>
                        setOpenDropdownIndex(
                          openDropdownIndex === index ? null : index,
                        )
                      }
                      className="flex group text-[2.25rem] font-[400] font-inter-tight leading-[140%] items-center gap-[1rem] relative"
                    >
                      <span className="transition-all duration-300 text-secondary">
                        {lang === 'de' && item.title ? item.title : item.titleDE}
                      </span>

                      <svg
                        className={`${openDropdownIndex === index ? 'rotate-180' : ''} w-[1.25rem] duration-300 transition-all`}
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="7"
                        fill="none"
                        viewBox="0 0 12 7"
                      >
                        <path
                          stroke="#434340"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 5 5 5-5"
                        ></path>
                      </svg>
                    </button>
                    <div className="absolute left-0 top-full invisible mt-4 bg-white border opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 z-[5] rounded-[.75rem] backdrop-blur-[2rem] p-[.125rem] border-white/20">
                      <div className="flex flex-col rounded-[0.5rem]  min-w-[30rem] gap-[1rem]">
                        {item?.dropdownLinks?.map((dropdownItem, dropIndex) => {
                          const openInNewTab = dropdownItem?.link?.openInNewTab;
                          return (
                            <Link
                              key={dropIndex}
                              target={openInNewTab ? '_blank' : '_self'}
                              rel={
                                openInNewTab ? 'noopener noreferrer' : undefined
                              }
                              href={addLanguageToLink(
                                resolvePageLink(
                                  dropdownItem?.link as IPageLink,
                                ) ?? '/',
                                lang,
                                hasLangPrefix,
                              )}
                              className={`${
                                pathname ===
                                dropdownItem?.link?.internalLink?.settings?.slug
                                  .current
                                  ? 'text-secondary'
                                  : 'text-secondary/70'
                              } flex items-center transition-colors duration-300 group gap-[.75rem] text-[2rem] py-[1.5rem] font-inter-tight font-[400] hover:text-secondary last:border-b-0 border-b-[.125rem] border-[#EAEBEA]`}
                            >
                              <span className="px-[2rem]">
                                {lang === 'de' && dropdownItem.titleDE
                                  ? dropdownItem.titleDE
                                  : dropdownItem.title}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {!isDropdown && (
                  <Link
                    href={itemLink}
                    target={openInNewTab ? '_blank' : '_self'}
                    rel={openInNewTab ? 'noopener noreferrer' : undefined}
                    className="group font-inter-tight text-[2rem] text-fakeblack leading-[120%] capitalize font-[400] transition-all duration-300 whitespace-nowrap"
                  >
                    {lang === 'de' && item.title ? item.title : item.titleDE}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="w-fit hidden lg:flex items-center justify-self-end gap-[2rem]">
        {/* <LanguageSwitcher /> */}

        {headerData?.menu.navbarCTA && (
          <CTAButton
            data={headerData?.menu.navbarCTA}
            className="lg:min-w-[23.75rem] !w-fit"
          />
        )}
      </div>

      <motion.button
        type="button"
        className="lg:hidden flex items-center justify-center w-[5rem] h-[5rem] relative"
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        aria-haspopup="dialog"
        aria-expanded={isMobileMenuOpen}
        aria-controls="mobile-menu"
        onClick={isMobileMenuOpen ? onCloseMobileMenu : onOpenMobileMenu}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {!isMobileMenuOpen ? (
            <motion.svg
              key="hamburger"
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="18"
              viewBox="0 0 30 18"
              fill="none"
              className="w-[3.75rem] h-[2.1875rem]"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
            >
              <path d="M30 15V17.5H0V15H30ZM30 10H0V7.5H30V10ZM30 2.5H0V0H30V2.5Z" fill="#011410"/>
            </motion.svg>
          ) : (
            <motion.svg
              key="close"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="w-[2.5rem] h-[2.5rem]"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
            >
              <path d="M20 1.75L18.25 0L10 8.25L1.75 0L0 1.75L8.25 10L0 18.25L1.75 20L10 11.75L18.25 20L20 18.25L11.75 10L20 1.75Z" fill="#F6F4E9"/>
            </motion.svg>
          )}
        </AnimatePresence>
      </motion.button>
    </nav>
  );
}

function MobileMenu({
  headerData,
  lang,
  hasLangPrefix,
  pathname,
  isOpen,
  onClose,
}: {
  headerData: GROQHeader;
  lang: 'de' | 'en';
  hasLangPrefix: boolean;
  pathname?: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const shouldReduceMotion = useReducedMotion();
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuItems = getMenuItems(headerData);

  const overlayVariants = useMemo(
    () =>
      shouldReduceMotion
        ? {
            open: { opacity: 1 },
            closed: { opacity: 1 },
          }
        : {
            open: {
              opacity: 1,
              transition: { duration: 0.25, ease: 'easeOut' },
            },
            closed: {
              opacity: 0,
              transition: { duration: 0.2, ease: 'easeIn' },
            },
          },
    [shouldReduceMotion],
  );

  const panelVariants = useMemo(
    () =>
      shouldReduceMotion
        ? {
            open: { height: '100vh' },
            closed: { height: '0vh' },
          }
        : {
            open: {
              height: '100vh',
              transition: { duration: .6, ease: easeInOut },
            },
            closed: {
              height: '0vh',
              transition: { duration: .6, ease: easeInOut },
            },
          },
    [shouldReduceMotion],
  );

  const listVariants = useMemo(
    () =>
      shouldReduceMotion
        ? {
            open: { transition: { staggerChildren: 0 } },
            closed: { transition: { staggerChildren: 0 } },
          }
        : {
            open: {
              transition: {
                staggerChildren: 0.06,
                delayChildren: 0.08,
              },
            },
            closed: {
              transition: {
                staggerChildren: 0.03,
                staggerDirection: -1,
              },
            },
          },
    [shouldReduceMotion],
  );

  const itemVariants = useMemo(
    () =>
      shouldReduceMotion
        ? {
            open: { opacity: 1, y: 0 },
            closed: { opacity: 1, y: 0 },
          }
        : {
            open: {
              opacity: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 420,
                damping: 34,
                mass: 0.7,
              },
            },
            closed: {
              opacity: 0,
              y: 10,
              transition: { duration: 0.12, ease: 'easeOut' },
            },
          },
    [shouldReduceMotion],
  );

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="mobile-menu"
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          className="fixed top-0 left-0 w-full z-[9999] bg-[#011410] overflow-hidden origin-top"
          variants={panelVariants}
          initial="closed"
          animate="open"
          exit="closed"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className="w-full h-full overflow-y-auto overscroll-contain"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            }}
          >
            <div className="flex flex-col h-full pt-[12rem] pb-[2rem] px-[2.5rem] gap-[3rem]">
              <nav>
                <motion.ul
                  className="flex flex-col gap-[2rem]"
                  variants={listVariants}
                >
                  {menuItems.map((item, index) => {
                    const itemLink = addLanguageToLink(
                      resolvePageLink(item?.link as IPageLink) ?? '/',
                      lang,
                      hasLangPrefix,
                    );

                    const type = item?.type;
                    const isDropdown =
                      type === 'dropdown' && item?.dropdownLinks;

                    const openInNewTab = item?.link?.openInNewTab;

                    // Normaliza pathname e itemLink removendo trailing slash (exceto para root)
                    const normalizedPathname = pathname && pathname !== '/' && pathname.endsWith('/')
                      ? pathname.slice(0, -1)
                      : pathname;

                    const normalizedItemLink = itemLink && itemLink !== '/' && itemLink.endsWith('/')
                      ? itemLink.slice(0, -1)
                      : itemLink;

                    // Verifica se é página home (/, /en, /de)
                    const isHomePage = normalizedItemLink === '/' || normalizedItemLink === '/en' || normalizedItemLink === '/de';

                    const isActive = isHomePage
                      ? normalizedPathname === normalizedItemLink
                      : normalizedPathname === normalizedItemLink ||
                        normalizedPathname?.startsWith(normalizedItemLink + '/');

                    return (
                      <motion.li key={index} variants={itemVariants}>
                        {isDropdown && (
                          <div className="relative group">
                            <button
                              onClick={() =>
                                setOpenDropdownIndex(
                                  openDropdownIndex === index ? null : index,
                                )
                              }
                              className={`flex group text-[2.25rem] font-[400] font-inter-tight leading-[140%] items-center gap-[1rem] relative`}
                            >
                              <span
                                className={`transition-all duration-300 text-white`}
                              >
                                {lang === 'de' && item.title
                                  ? item.title
                                  : item.titleDE}
                              </span>

                              <svg
                                className={`${openDropdownIndex === index ? 'rotate-180' : ''} w-[1.25rem] duration-300 transition-all`}
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="7"
                                fill="none"
                                viewBox="0 0 12 7"
                              >
                                <path
                                  stroke="#EAEBEA"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="m1 1 5 5 5-5"
                                ></path>
                              </svg>
                            </button>
                            <div className="absolute left-0 top-full invisible mt-4 bg-white border opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 z-[5] rounded-[.75rem] backdrop-blur-[2rem] p-[.125rem] border-white/20">
                              <div className="flex flex-col rounded-[0.5rem]  min-w-[30rem] gap-[1rem]">
                                {item?.dropdownLinks?.map(
                                  (dropdownItem, dropIndex) => {
                                    return (
                                      <Link
                                        key={dropIndex}
                                        target={
                                          openInNewTab ? '_blank' : '_self'
                                        }
                                        rel={
                                          openInNewTab
                                            ? 'noopener noreferrer'
                                            : undefined
                                        }
                                        href={addLanguageToLink(
                                          resolvePageLink(
                                            dropdownItem?.link as IPageLink,
                                          ) ?? '/',
                                          lang,
                                          hasLangPrefix,
                                        )}
                                        className={`${
                                          pathname ===
                                          dropdownItem?.link?.internalLink
                                            ?.settings?.slug.current
                                            ? 'text-secondary'
                                            : 'text-secondary/70'
                                        } flex items-center transition-colors duration-300 group gap-[.75rem] text-[2rem]  py-[1.5rem] font-inter-tight font-[400]  hover:text-secondary last:border-b-0 border-b-[.125rem] border-[#EAEBEA]`}
                                      >
                                        <span className="px-[2rem]">
                                          {lang === 'de' && dropdownItem.title
                                            ? dropdownItem.title
                                            : dropdownItem.titleDE}
                                        </span>
                                      </Link>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {!isDropdown && (
                          <Link
                            target={openInNewTab ? '_blank' : '_self'}
                            rel={
                              openInNewTab ? 'noopener noreferrer' : undefined
                            }
                            href={itemLink}
                            onClick={onClose}
                            className={`text-[5rem] text-offwhite ${isActive ? 'bg-[#072922]' : 'opacity-40'} leading-[120%] capitalize font-[300] relative inline-flex flex-row items-end gap-[1rem] font-inter-tight p-[1rem] w-full`}
                          >
                            <svg className="w-[2.5rem] h-[2.5rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                              <path d="M16.25 13.75L15 13.75L15 5.88125L4.63125 16.25L3.75 15.3688L14.1188 5L6.25 5L6.25 3.75L16.25 3.75L16.25 13.75Z" fill="#F6F4E9"/>
                            </svg>
                            {item.title}
                          </Link>
                        )}
                      </motion.li>
                    );
                  })}
                </motion.ul>
              </nav>

              <CTAButton
                data={headerData?.menu.navbarCTA}
                className="w-full"
              />
              
              <Image src={menuImage} alt="RevUp" className="w-full h-auto mt-auto" />
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
